import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
  add,
  parseISO,
  startOfDay,
} from 'date-fns';
import { useApp, useFields } from 'hooks';
import { getAllAppointements as getAll } from 'api';
import { getAllAppointements as getAllFake } from 'api/fake';
import Filters from 'components/filters';
import { appointmentBusinessTypes } from 'constants/appointments';
import AppointmentItem from 'components/appointments/appointment-item';
import AppointmentLoader from 'components/appointments/appointment-loader';
import ListPlaceholder from 'components/ui-kit/list-placeholder';
import Pagination from 'components/ui-kit/pagination';
import * as S from './styles';

// A range can now reach years ahead, so the rows are shown a page at a time; the response stays whole.
const PAGE_SIZE = 50;

const startOfToday = startOfDay(new Date());
const daterange = {
  from: startOfToday,
  to: add(startOfToday, { days: 30 }),
};

export default function UpcomingList() {
  const { portal, appointment, isPreviewMode } = useApp();
  const { fields, handleClickChange, handleChange } = useFields({
    type: 'all',
    daterange: [daterange.from, daterange.to],
  });

  const getAllAppointements = isPreviewMode ? getAllFake : getAll;
  const [page, setPage] = useState(1);
  const from = fields.daterange[0].toISOString();
  const to = fields.daterange[1].toISOString();
  const type = appointmentBusinessTypes[fields.type];

  useEffect(() => setPage(1), [from, to, type]);

  const { data, isLoading, isFetching } = useQuery(
    [
      'appointments',
      portal?.id,
      { from, to, type },
    ],
    getAllAppointements,
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!portal?.id,
    },
  );

  const renderContent = () => {
    if (isLoading || isFetching) {
      return <AppointmentLoader />;
    }

    if (!data?.data || !data?.data.length) {
      return <ListPlaceholder message="No upcoming appointments. Request a service" />;
    }

    const totalPages = Math.ceil(data.data.length / PAGE_SIZE);
    const pageItems = data.data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
      <>
        <S.List>
          {pageItems.map((item, index) => (
            <S.ListItem key={`${item.id}-${item.start || ''}-${String(index)}`}>
              <AppointmentItem
                id={item.id}
                title={item.title}
                type={item.businessEntityType}
                address={item.serviceAddress}
                unitNumber={item.unitNumber}
                totalAmount={item.totalAmount}
                date={parseISO(item.start)}
                endDate={item.end ? parseISO(item.end) : null}
                isAllDay={item.isAllDay}
                isCompact={appointment.isCompact}
                employees={item.employees}
                invoice={item.invoiceSent}
                invoiceId={item.invoiceId}
                status={item.status}
                variant="row"
              />
            </S.ListItem>
          ))}
        </S.List>
        <S.Pagination>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </S.Pagination>
      </>
    );
  };

  return (
    <>
      <Filters
        fields={fields}
        handleClickChange={handleClickChange}
        handleChange={handleChange}
        daterange={{
          minDate: new Date(),
        }}
        select={{
          options: [
            { label: 'All Types', value: 'all' },
            { label: 'Job', value: 'job' },
            { label: 'Estimate', value: 'estimate' },
            { label: 'Visit', value: 'visit' },
          ],
          name: 'type',
          label: 'Type',
        }}
        showViewToggle
      />
      {renderContent()}
    </>
  );
}
